import { z } from 'zod';
import { zPrinterInterface } from '../enums';
import { viewerDocketCvxSchema } from './viewerDocketSchema';

/**
 * What the docket viewer is for at a venue, decided by its printers:
 * - `FAILED_DOCKETS`: the venue prints on physical printers, and the viewer shows the dockets that
 *   didn't print. Cleared on the POS (printed again or removed) or on the phone.
 * - `ALL_DOCKETS`: the venue has the docket viewer printer instead, and every kitchen docket sent to
 *   it goes to the viewer. Cleared on the phone only.
 */
export const zDocketViewerMode = z.literal(['FAILED_DOCKETS', 'ALL_DOCKETS']);
export type DocketViewerMode = z.output<typeof zDocketViewerMode>;

/**
 * A physical printer as the viewer shows it: a name, a light, and a feed filter. Never the docket
 * viewer printer itself.
 */
export const docketViewerPrinterSchema = z.object({
  identifier: z.string(),
  interface: zPrinterInterface,
  name: z.string(),
  /** Projected from `discoveryFailedAt`, so the phone sees a printer recover without a refresh. */
  online: z.boolean(),
});
export type DocketViewerPrinter = z.output<typeof docketViewerPrinterSchema>;

/**
 * A viewer docket, trimmed for the viewer: enough to render, nothing more.
 *
 * `desiredPosPrinterIdentifier` is what the printer select filters on: the printer this docket was
 * meant for.
 */
export const docketViewerDocketSchema = viewerDocketCvxSchema.pick({
  desiredPosPrinterIdentifier: true,
  docketPayload: true,
  docketType: true,
  localCreatedAt: true,
  uuid: true,
});
export type DocketViewerDocket = z.output<typeof docketViewerDocketSchema>;

/**
 * Envelope for `docketViewerSessions.getViewerData`. A missing session returns `null`: the viewer
 * holds a live subscription, so a bad code has to render as a state, not a query error.
 */
export const docketViewerDataResponseSchema = z.object({
  dockets: z.array(docketViewerDocketSchema),
  printers: z.array(docketViewerPrinterSchema),
  viewerMode: zDocketViewerMode,
});
export type DocketViewerDataResponse = z.output<typeof docketViewerDataResponseSchema>;
